import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Sparkles, Loader2, RotateCcw, Lock } from "lucide-react";

/**
 * ExplainWithAI — botão que chama /api/ai/explain-question
 * Deve ser usado apenas quando a resposta do aluno está incorreta.
 *
 * Features:
 * - Temporizador visual (0→30s) durante o carregamento
 * - Contador de usos restantes hoje (AI_DAILY_LIMIT)
 * - Estado bloqueado quando limite diário atingido
 */
const TIMER_MAX = 30; // segundos de referência para a barra de progresso

const ExplainWithAI = ({ question }) => {
  const [loading, setLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [error, setError] = useState(null);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [remainingToday, setRemainingToday] = useState(null); // null = ainda não carregado
  const [dailyLimit, setDailyLimit] = useState(null);

  // Temporizador de progresso durante o carregamento
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef(null);

  // Carregar estado de utilização ao montar
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/ai/explain-usage`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        setRemainingToday(r.data.remaining_today);
        setDailyLimit(r.data.daily_limit);
        if (r.data.remaining_today === 0) setIsLimitReached(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Gerir temporizador durante o carregamento
  useEffect(() => {
    if (loading) {
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [loading]);

  const requestExplanation = async () => {
    setLoading(true);
    setError(null);
    setExplanation(null);
    try {
      const correctAnswer = question.answers.find(
        (a) => (a?.id ?? a) === question.correct_answer
      );
      const correctAnswerText = correctAnswer?.text ?? correctAnswer ?? "";

      const response = await axios.post(
        `${API}/ai/explain-question`,
        {
          statement: question.statement,
          answers: question.answers.map((a) => ({
            text: a?.text ?? a,
            is_correct: (a?.id ?? a) === question.correct_answer,
          })),
          correct_answer_text: correctAnswerText,
          explanation: question.note || null,
          question_id: question.question_id || null,
        },
        { withCredentials: true }
      );
      setExplanation(response.data.explanation);
      setRemainingToday(response.data.remaining_today);
      setDailyLimit(response.data.daily_limit);
      if (response.data.remaining_today === 0) setIsLimitReached(true);
    } catch (e) {
      if (e.response?.status === 429) {
        setIsLimitReached(true);
        setRemainingToday(0);
      }
      setError(e.response?.data?.detail || "Erro ao obter explicação da IA");
    } finally {
      setLoading(false);
    }
  };

  const progressPct = Math.min(100, (elapsed / TIMER_MAX) * 100);

  // --- Estado: limite atingido ---
  if (isLimitReached && !explanation) {
    return (
      <div className="mt-4" data-testid="explain-with-ai-section">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-700 bg-slate-800/40 text-xs text-slate-500"
          data-testid="ai-limit-reached"
        >
          <Lock className="w-3.5 h-3.5 shrink-0 text-slate-600" />
          <span>Limite diário de explicações atingido. Retoma amanhã.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="explain-with-ai-section">
      {/* Botão principal — visível antes de ter explicação e fora do loading */}
      {!explanation && !loading && !isLimitReached && (
        <Button
          variant="outline"
          size="sm"
          onClick={requestExplanation}
          data-testid="explain-with-ai-btn"
          className="text-purple-400 border-purple-500/30 hover:bg-purple-500/10 hover:border-purple-500/50 transition-all"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Pedir explicação à IA
          {remainingToday !== null && dailyLimit !== null && (
            <span className="ml-2 text-xs text-slate-500 font-normal">
              {remainingToday}/{dailyLimit} hoje
            </span>
          )}
        </Button>
      )}

      {/* Estado de carregamento com temporizador */}
      {loading && (
        <div
          className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 space-y-2"
          data-testid="ai-loading-timer"
        >
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <Loader2 className="w-4 h-4 animate-spin text-purple-400 shrink-0" />
            <span>A gerar explicação com IA...</span>
            <span className="ml-auto font-mono text-xs text-purple-400">{elapsed}s</span>
          </div>
          {/* Barra de progresso */}
          <div className="w-full h-1 bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500 rounded-full transition-all duration-1000"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-xs text-slate-500">Pode demorar até 30 segundos.</p>
        </div>
      )}

      {/* Erro */}
      {error && !loading && !isLimitReached && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400 mb-2">{error}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={requestExplanation}
            className="text-red-400 hover:text-red-300 p-0 h-auto"
          >
            <RotateCcw className="w-3 h-3 mr-1" />
            Tentar novamente
          </Button>
        </div>
      )}

      {/* Explicação gerada */}
      {explanation && !loading && (
        <div
          className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg"
          data-testid="ai-explanation-result"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <p className="text-xs uppercase tracking-wider text-purple-400 font-medium">
                Explicação da IA
              </p>
            </div>
            <div className="flex items-center gap-2">
              {remainingToday !== null && (
                <span className="text-xs text-slate-600 font-mono">{remainingToday} restantes</span>
              )}
              {!isLimitReached && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={requestExplanation}
                  className="text-slate-500 hover:text-slate-300 p-1 h-auto"
                  title="Gerar nova explicação"
                >
                  <RotateCcw className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
            {explanation}
          </p>
        </div>
      )}
    </div>
  );
};

export default ExplainWithAI;
